/**
 * FormsGate — capability gate for Forms pages.
 *
 * Route-level ProtectedRoute only knows roles (and ANDs roles with rights), but Forms
 * capabilities are "role OR right" (a user may hold Forms.Entries.Approve without a
 * Forms role). This gate asks useFormsPermissions instead.
 */

import type { ReactNode } from 'react';
import { IconLock } from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import { EmptyState } from '@/components/ui/data-display';
import { useFormsPermissions, type FormsPermissions } from '@/hooks/forms/useFormsPermissions';

interface FormsGateProps {
  allow: (p: FormsPermissions) => boolean;
  children: ReactNode;
}

export function FormsGate({ allow, children }: FormsGateProps) {
  const perms = useFormsPermissions();
  if (!allow(perms)) {
    return (
      <LayoutContainer>
        <EmptyState
          icon={<IconLock size={40} />}
          title="Access denied"
          description="You do not have permission to open this page. Ask a Forms administrator for access."
        />
      </LayoutContainer>
    );
  }
  return <>{children}</>;
}
